<template>
  <h2 class="discussions-column__title">{{ $t('default.discussions.general_discussion') }}</h2>

  <div v-if="threadsError" class="error-banner">
    {{ $t('default.discussions.load_error') }}
  </div>

  <div v-else-if="!threadsLoading && threads.length === 0" class="empty-state">
    <p>{{ $t('default.discussions.empty') }}</p>
  </div>

  <ul v-else class="message-feed">
    <template v-for="thread in sortedThreads" :key="thread.id">
      <li
        v-for="post in visiblePosts(thread.id)"
        :key="post.id"
        class="message-feed__item"
      >
        <div class="bubble">
          <div class="bubble__header">
            <div class="bubble__author-info">
              <span class="bubble__author">
                {{ post.author ? post.author.full_name : $t('default.discussions.unknown_author') }}
              </span>
              <span class="bubble__date">{{ formatDate(post.created) }}</span>
            </div>
            <router-link
              v-if="thread.page"
              :to="'/material/' + thread.page.path"
              class="bubble__page-pill"
            >
              <svg
                class="bubble__page-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              {{ thread.page.title }}
            </router-link>
          </div>
          <MarkdownRenderer class="bubble__content" :content="post.body" />
        </div>
      </li>

      <li
        v-if="threadPostsLoading(thread.id) && visiblePosts(thread.id).length === 0"
        class="message-feed__thread-loading"
      >
        <Loader />
      </li>
    </template>
  </ul>

  <div v-if="threadsNextPage" class="load-more">
    <button
      class="green"
      :disabled="threadsLoading"
      @click="loadMore"
    >
      {{ threadsLoading ? $t('default.discussions.loading') : $t('default.discussions.load_more') }}
    </button>
  </div>

  <div v-if="threadsLoading && threads.length === 0" class="loading-state">
    <Loader />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, watch } from 'vue'
import { useStore } from 'vuex'
import axios from '@/axios-instance'
import Loader from '@/components/Loader'
import MarkdownRenderer from '@/components/shared/MarkdownRenderer'
import { usePersistedOrg } from '@/composables/usePersistedOrg'
import { stringToDatetime } from '@/datetimeUtils'

const store = useStore()

const { selectedOrg } = usePersistedOrg()

const currentUser = computed(() => store.state.user.user)
const userType = computed(() => currentUser.value?.type?.type)
const needsOrgPicker = computed(() =>
  userType.value === 'coach' || userType.value === 'admin'
)

const threads = computed(() => store.state.discussions.threads)
const threadsLoading = computed(() => store.state.discussions.threadsLoading)
const threadsError = computed(() => store.state.discussions.threadsError)
const threadsNextPage = computed(() => store.state.discussions.threadsNextPage)

// Threads come back from the API ordered by pin/creation time, not by latest
// activity. Reorder here so the thread with the most recent post is first.
const sortedThreads = computed(() =>
  [...threads.value].sort((a, b) => new Date(b.modified) - new Date(a.modified))
)

// Local, per-thread posts cache. Deliberately not using the shared
// discussions/posts store slot: that slot holds a single "current thread"
// worth of posts (used by ThreadDetailView) and would collide if reused
// for many threads shown concurrently here.
const threadPosts = reactive({})

function visiblePosts(threadId) {
  const entry = threadPosts[threadId]
  if (!entry) return []
  return entry.posts.filter((post) => !post.is_deleted)
}

function threadPostsLoading(threadId) {
  return threadPosts[threadId]?.loading ?? false
}

async function fetchPostsForThread(threadId) {
  threadPosts[threadId] = { posts: [], loading: true, error: false }
  try {
    const res = await axios.get(`/api/v1/discussions/threads/${threadId}/posts/`)
    const data = Array.isArray(res.data) ? res.data : (res.data.results ?? [])
    threadPosts[threadId] = { posts: data, loading: false, error: false }
  } catch {
    threadPosts[threadId] = { posts: [], loading: false, error: true }
  }
}

function formatDate(dateStr) {
  return stringToDatetime(dateStr)
}

function loadThreads() {
  if (needsOrgPicker.value && !selectedOrg.value) return
  store.dispatch('discussions/fetchThreads', {
    organizationId: selectedOrg.value?.id ?? null,
  })
}

function loadMore() {
  store.dispatch('discussions/fetchMoreThreads')
}

watch(selectedOrg, () => {
  store.commit('discussions/setThreads', [])
  for (const key of Object.keys(threadPosts)) {
    delete threadPosts[key]
  }
  loadThreads()
})

// Fetch posts for any thread we don't already have posts for, whenever the
// thread list changes (initial load or "load more").
watch(threads, (newThreads) => {
  for (const thread of newThreads) {
    if (!threadPosts[thread.id]) {
      fetchPostsForThread(thread.id)
    }
  }
}, { immediate: true })

onMounted(() => {
  loadThreads()
})
</script>

<style scoped>
.discussions-column__title {
  margin: 0 0 1.5rem;
}

.message-feed {
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.message-feed__item {
  display: flex;
  flex-direction: column;
}

.message-feed__thread-loading {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
}

.bubble {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  padding: 0.6rem 0.85rem;
  border-radius: 12px;
  background: var(--gray-300);
  color: var(--text-black);
  word-break: break-word;
}

.bubble__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.bubble__author-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.bubble__author {
  font-weight: 700;
  font-size: 0.9rem;
}

.bubble__date {
  font-size: 0.7rem;
  color: var(--text-grey);
  white-space: nowrap;
}

.bubble__page-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.15rem 0.6rem;
  border-radius: 9999px;
  background: white;
  color: var(--text-black);
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}

.bubble__page-pill:hover {
  color: var(--text-black);
  text-decoration: underline;
}

.bubble__page-icon {
  width: 0.8rem;
  height: 0.8rem;
  flex-shrink: 0;
}

.bubble__content {
  font-size: 0.9rem;
  line-height: 1.4;
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-grey);
}

.error-banner {
  background: #fff5f5;
  border: 1px solid var(--halsa-red);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  color: var(--halsa-red);
  margin-bottom: 1rem;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.load-more {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}
</style>
